import {
  Alert,
  Autocomplete,
  Box,
  capitalize,
  Checkbox,
  Chip,
  Divider,
  Grid,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "../../common/components/Button";
import { toast } from "../../common/components/Toast";
import { SPACING } from "../../common/constants/spacing";
import { useProject } from "../../common/contexts/ProjectContext";
import { useAssets, useLinkEpisodes } from "../api/assets";

const AssetMetadata = ({ asset, onRefresh }) => {
  const { t } = useTranslation("assets");

  const DetailRow = ({ label, value }) => (
    <Grid container spacing={2} sx={{ mt: 2, mb: SPACING.xs }}>
      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <Typography variant="bodyMd" fontWeight={600}>
          {label}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8, md: 9 }}>
        {typeof value === "string" ? (
          <Typography variant="bodyMd">{value}</Typography>
        ) : (
          value
        )}
      </Grid>
    </Grid>
  );

  const SectionHeader = ({ title }) => (
    <Typography
      variant="h6"
      sx={{
        textTransform: "uppercase",
        fontWeight: 500,
        fontSize: "0.8rem",
        color: "text.secondary",
        mt: 4,
        mb: 0,
      }}
    >
      {title}
    </Typography>
  );

  const { project, currentUserRole } = useProject();
  const isOwner = currentUserRole === "owner";

  // Series linking state
  const [selectedSeries, setSelectedSeries] = useState(null);
  const { assets: seriesOptions } = useAssets(1, 100, "", "series");
  const { linkEpisodes, linking } = useLinkEpisodes();

  // Producer assignment state
  const [selectedProducers, setSelectedProducers] = useState([]);

  const producers = (project?.users || []).filter(
    (u) => u.role === "producer" || u.role === "co_producer",
  );

  const handleLinkToSeries = async () => {
    if (!selectedSeries || !asset) return;
    try {
      await linkEpisodes(selectedSeries.id, [asset.id]);
      toast.success(t("linkMode.success"));
      setSelectedSeries(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(t("linkMode.error"));
    }
  };

  const handleAssignProducers = async () => {
    if (selectedProducers.length === 0 || !asset) return;
    const token = localStorage.getItem("accessToken");
    try {
      for (const pu of selectedProducers) {
        const currentIds = (pu.accessible_assets || []).map((a) =>
          typeof a === "object" ? a.asset_id : a,
        );
        if (currentIds.includes(asset.id)) continue;
        await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/projects/producer-access/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              project_user_id: pu.id,
              asset_ids: [...currentIds, asset.id],
            }),
          },
        );
      }
      toast.success(
        t("metadata.producersAssigned", { count: selectedProducers.length }),
      );
      setSelectedProducers([]);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Failed to assign producers");
    }
  };

  const assetTypeLabel =
    asset?.asset_type === "series"
      ? t("metadata.series")
      : asset?.asset_type === "episode"
        ? t("metadata.episode")
        : t("metadata.standalone");

  const importHistory = asset?.import_history;
  const hasImportFiles = importHistory?.files?.length > 0;

  return (
    <Box>
      {/* Section 1: Identity */}
      <SectionHeader title={t("metadata.identity")} />
      <Divider />

      <DetailRow label={t("metadata.assetType")} value={assetTypeLabel} />

      {asset?.asset_type === "episode" && asset?.parent_asset_title && (
        <DetailRow
          label={t("metadata.series")}
          value={
            <Typography
              component={Link}
              to={`/assets/${asset.parent_asset}`}
              variant="bodyMd"
              sx={{ color: "primary.main", textDecoration: "none" }}
            >
              {asset.parent_asset_title}
            </Typography>
          }
        />
      )}

      <DetailRow
        label={t("metadata.platform")}
        value={capitalize(asset?.source?.platform || t("metadata.unknown"))}
      />

      <DetailRow
        label={t("metadata.status")}
        value={
          <Chip
            label={
              asset?.is_active ? t("metadata.active") : t("metadata.inactive")
            }
            size="small"
            color={asset?.is_active ? "success" : "default"}
            variant="outlined"
          />
        }
      />

      <DetailRow
        label={t("metadata.createdAt")}
        value={new Date(asset?.created_at).toLocaleDateString()}
      />

      {asset?.needs_linking && isOwner && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="bodySm" sx={{ mb: 1 }}>
            {t("metadata.needsLinking")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Autocomplete
              size="small"
              options={seriesOptions}
              getOptionLabel={(o) => o.title}
              value={selectedSeries}
              onChange={(_, v) => setSelectedSeries(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("selectSeries")}
                  size="small"
                />
              )}
              sx={{ minWidth: 250 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleLinkToSeries}
              disabled={!selectedSeries || linking}
            >
              {t("linkEpisodes")}
            </Button>
          </Box>
        </Alert>
      )}

      {/* Section 2: Import History (owner-only) */}
      {isOwner && (
        <>
          <SectionHeader title={t("metadata.importHistory")} />
          <Divider />

          {hasImportFiles ? (
            <>
              {importHistory.files.map((f) => (
                <DetailRow
                  key={f.file_id}
                  label={t("metadata.importedFrom")}
                  value={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="bodyMd" sx={{ fontWeight: 500 }}>
                        {f.file_name}
                      </Typography>
                      {f.source_label && (
                        <Chip
                          label={f.source_label}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      <Typography variant="bodySm" color="text.secondary">
                        {new Date(f.imported_at).toLocaleDateString()} &middot;{" "}
                        {t("metadata.records", { count: f.record_count })}
                      </Typography>
                    </Box>
                  }
                />
              ))}

              {importHistory.period_start && importHistory.period_end && (
                <DetailRow
                  label={t("metadata.periodCoverage")}
                  value={t("metadata.periodCoverage", {
                    start: new Date(
                      importHistory.period_start,
                    ).toLocaleDateString(),
                    end: new Date(
                      importHistory.period_end,
                    ).toLocaleDateString(),
                  })}
                />
              )}

              {importHistory.source_labels?.length > 0 && (
                <DetailRow
                  label={t("metadata.sourceLabels")}
                  value={
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {importHistory.source_labels.map((label) => (
                        <Chip
                          key={label}
                          label={label}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  }
                />
              )}
            </>
          ) : (
            <Typography variant="bodySm" color="text.secondary" sx={{ mt: 2 }}>
              {t("metadata.noImportHistory")}
            </Typography>
          )}
        </>
      )}

      {/* Section 3: Producers (owner-only) */}
      {isOwner && (
        <>
          <SectionHeader title={t("metadata.producers")} />
          <Divider />

          {asset?.producers?.length > 0 ? (
            <DetailRow
              label={t("metadata.producers")}
              value={
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {asset.producers.map((p) => (
                    <Chip
                      key={p.user_id}
                      label={p.name}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              }
            />
          ) : isOwner ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="bodySm" sx={{ mb: 1 }}>
                {t("metadata.noProducersWarning")}
              </Typography>
              {producers.length > 0 && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                >
                  <Autocomplete
                    multiple
                    size="small"
                    options={producers}
                    getOptionLabel={(o) => o.user_details?.name || ""}
                    value={selectedProducers}
                    onChange={(_, v) => setSelectedProducers(v)}
                    disableCloseOnSelect
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          size="small"
                          checked={selected}
                          sx={{ mr: 1 }}
                        />
                        <ListItemText primary={option.user_details?.name} />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("metadata.assignProducers")}
                        size="small"
                      />
                    )}
                    sx={{ minWidth: 250 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAssignProducers}
                    disabled={selectedProducers.length === 0}
                  >
                    {t("metadata.assignProducers")}
                  </Button>
                </Box>
              )}
            </Alert>
          ) : (
            <Typography variant="bodySm" color="text.secondary" sx={{ mt: 2 }}>
              {t("metadata.noProducers")}
            </Typography>
          )}
        </>
      )}

      {/* Section 4: Configuration (owner-only) */}
      {isOwner && (
        <>
          <SectionHeader title={t("metadata.configuration")} />
          <Divider />

          <DetailRow
            label={t("metadata.statementFrequency")}
            value={asset?.statement_frequency || t("metadata.notSet")}
          />
          <DetailRow
            label={t("metadata.paymentThreshold")}
            value={
              asset?.payment_threshold
                ? `$${parseFloat(asset.payment_threshold).toFixed(2)}`
                : t("metadata.notSet")
            }
          />
          <DetailRow
            label={t("metadata.paymentWindow")}
            value={
              asset?.payment_window
                ? t("metadata.paymentWindowDays", {
                    days: asset.payment_window,
                  })
                : t("metadata.notSet")
            }
          />
          <DetailRow
            label={t("metadata.notesLabel")}
            value={asset?.notes || t("metadata.noNotes")}
          />
        </>
      )}
    </Box>
  );
};

export default AssetMetadata;
